// components/entities/ProjectRenderer.tsx
import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import { BaseEntityProps, LeasingProjectDTO } from '#types/types';
import { Folder, Loader2, Star } from '@/components/ui/lucide-memo';
import { RowMoreButton } from '@/components/ui/RowMoreButton';
import { Frame, PenTool, Rows3 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import SimpleLoader from '../ui/SimpleLoader';
import { formatCreditsCompact } from '#lib/formatCredits';
import { STUDIO_PROJECT_KINDS, projectKindOf } from '#lib/studio-project-view';
import { probeCoverIsBlank } from '#lib/asset-blank-cover';
import { buildAssetRenditionUrl } from '#lib/asset-rendition';
import { deletionFailure } from '#lib/modelist/fater3dState';
import clsx from 'clsx';
import { EntitySkeleton } from './EntityView';

export const ProjectRenderer = React.memo(({
  entityId,
  variant = 'card',
  className
}: BaseEntityProps<LeasingProjectDTO>) => {
  const { kernel } = useKernel();
  const data = useKernelStore(state => state.inventory.Project.registry[entityId]) as LeasingProjectDTO | undefined;
  const userContext = useKernelStore(state => state.userContext);
  const isOpening = useKernelStore(state => state.studioOpeningProjectId === entityId);

  const [isCopyingForAdmin, setIsCopyingForAdmin] = useState(false);
  const isTrashed = (data as any)?.toDelete === true;

  // NOTE: a client-side "blank cover" heuristic used to live here. It loaded the
  // cover into a 16x16 canvas and hid the whole card when the probe read the
  // cover as near-black/transparent (maxLum < 8 || maxAlpha < 8). It produced
  // false positives — `drawImage` right after `img.onload` reads all-zero pixels
  // for some real webp covers — so genuine, content-rich projects (e.g. a user's
  // most recent work) silently vanished from their own grid. A project the API
  // returns is always shown; projects without a cover use the empty-project
  // placeholder below, while a blank cover is never allowed to hide the card.

  // Existing blank covers — a transparent ~1KB version poster captured from an
  // empty canvas — fall back to the project's topmost real layer image. The blank
  // is detected by asset size (a transparent placeholder is tiny, ~1KB, versus
  // tens/hundreds of KB for a real cover), which is reliable and needs no pixel
  // decode. The size comes from `probeCoverIsBlank`, which asks the asset route for
  // metadata only and memoises per URL — the card never downloads the image itself.
  // Runs for any cover; when it is blank we swap to the fallback if one exists,
  // otherwise the empty-project placeholder is shown.
  const coverUrl = data?.imageUrl ?? null;
  const fallbackUrl = data?.fallbackImageUrl ?? null;
  const [coverIsBlankPoster, setCoverIsBlankPoster] = useState(false);
  useEffect(() => {
    setCoverIsBlankPoster(false);
    if (!coverUrl) return;
    let cancelled = false;
    void probeCoverIsBlank(coverUrl).then((isBlank) => {
      if (!cancelled && isBlank) setCoverIsBlankPoster(true);
    });
    return () => {
      cancelled = true;
    };
  }, [coverUrl]);
  // List outputs are project layers with no version poster, so the layer is their only cover.
  const effectiveCoverUrl = (coverIsBlankPoster || !coverUrl) && fallbackUrl ? fallbackUrl : coverUrl;

  // Subtle pulse while the cover image loads; fade the image in once ready.
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => {
    setImgLoaded(false);
  }, [effectiveCoverUrl]);

  const markImgLoaded = useCallback(() => setImgLoaded(true), []);
  // Cached images can finish before onLoad attaches; mark loaded immediately so
  // the fade-in doesn't leave them stuck at opacity-0. The ref callback must be
  // stable: an inline one is detached (called with null) and reattached on every
  // render, which re-runs this probe for nothing.
  const handleImgRef = useCallback((el: HTMLImageElement | null) => {
    if (el && el.complete && el.naturalWidth > 0) setImgLoaded(true);
  }, []);

  // --- Actions ---
  const handleOpenProject = useCallback(() => {
    if (!data) return;
    void kernel.current?.focusEntityUI('Project', data.id);
  }, [kernel, data?.id]);

  const handleRename = useCallback(() => {
    if (!data) return;
    const newName = prompt('Enter new project name:', data.name);
    if (newName && newName.trim() !== '') {
      kernel.current?.updateEntity('Project', data.id, { name: newName.trim() });
    }
  }, [kernel, data?.id, data?.name]);

  const handleCopyProject = useCallback(() => {
    if (!data) return;
    kernel.current?.copyProject(data.id).then(() => {
      void kernel.current?.refreshInventory('Project');
    });
  }, [kernel, data?.id]);

  const handleDeleteProject = useCallback(() => {
    if (!data) return;
    kernel.current?.deleteEntity('Project', data.id).catch((error) => kernel.current?.handleError(deletionFailure(error, 'The project could not be deleted.')));
  }, [kernel, data?.id]);

  const handleTrashProject = useCallback(async (toDelete: boolean) => {
    if (!data) return;
    try {
      await kernel.current?.updateEntity('Project', data.id, { toDelete } as any);
    } catch (error) {
      kernel.current?.handleError(deletionFailure(error, toDelete ? 'The project could not be moved to the trash.' : 'The project could not be restored.'));
      return;
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('fater:project-list-reload'));
    }
  }, [kernel, data?.id]);

  const handleToggleFavorite = useCallback(async () => {
    if (!data) return;
    const newFav = !data.favorite;

    try {
      await kernel.current?.updateEntity('Project', data.id, { favorite: newFav });
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  }, [kernel, data?.id, data?.favorite]);

  const handleAdminCopyProject = useCallback(async () => {
    if (!data) return;
    setIsCopyingForAdmin(true);
    try {
      await kernel.current?.adminCopyProjectToSelf(data.id);
    } finally {
      setIsCopyingForAdmin(false);
    }
  }, [data?.id, kernel]);

  const handleMenuClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (!data) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();

    const actions = isTrashed
      ? [
          { label: 'Restore', onClick: () => handleTrashProject(false) },
          { label: 'Delete permanently', onClick: handleDeleteProject, danger: true },
        ]
      : [
          { label: 'Open', onClick: handleOpenProject },
          { label: data.favorite ? 'Remove from favorites' : 'Add to favorites', onClick: () => void handleToggleFavorite() },
          { label: 'Rename', onClick: handleRename },
          { label: 'Copy', onClick: handleCopyProject },
          { label: 'Move to trash', onClick: () => handleTrashProject(true), danger: true, separatorBefore: true },
        ];

    if (userContext?.isImpersonating) {
      const adminName = userContext.trueUser?.name || 'Fater Admin';
      actions.splice(3, 0, {
        label: `Copy for ${adminName}`,
        onClick: handleAdminCopyProject,
      });
    }

    kernel.current?.setMenuData({
      position: { x: rect.right, y: rect.top },
      actions,
    });
  }, [isTrashed, data?.id, data?.favorite, handleToggleFavorite, handleTrashProject, handleDeleteProject, handleOpenProject, handleRename, handleCopyProject, userContext, handleAdminCopyProject, kernel]);

  // --- Render ---
  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  const authorName = data.userName || 'Unknown';
  const formattedTime = data.updatedAt ? formatDistanceToNow(new Date(data.updatedAt), { addSuffix: true }) : '';
  const formattedCredits = data.totalCreditsSpent > 0 ? formatCreditsCompact(data.totalCreditsSpent) : null;
  const projectKind = projectKindOf(data.projectType);
  const ProjectIcon = { canvas: Frame, list: Rows3, vector: PenTool }[projectKind];

  if (variant === 'chip') {
    return (
      <div className={clsx('flex items-center gap-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs text-white/80', className)}>
        <Folder size={12} />
        <span className="truncate">{data.name}</span>
      </div>
    );
  }

  // ChatGPT's Library tile (owner, 2026-09-28: 'chatgptize the dashboard of Studio'): no card
  // around it, the picture itself is the card, rounded, with the name and one quiet line under it.
  // Nothing sits on the picture: the row's "…" shows on hover beside the name, as on ChatGPT's
  // conversation rows, and favourites are marked by a small star before the name.
  const isOwnProject = Boolean(data.userId) && data.userId === userContext?.dbUserId;
  return (
    <div
      className={clsx('group relative cursor-pointer animate-card-appear', className)}
      onClick={handleOpenProject}
    >
      {isCopyingForAdmin && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[16px] bg-[#1B1B1B]/80 backdrop-blur-sm">
            <SimpleLoader message="Copying..." size={40} />
        </div>
      )}

      <div className="relative aspect-[4/3] overflow-hidden rounded-[16px] bg-[#1B1B1B]">
        {effectiveCoverUrl && !(coverIsBlankPoster && !fallbackUrl) ? (
          <>
            {!imgLoaded && <div className="absolute inset-0 animate-pulse bg-[#212121]" />}
            <img
              ref={handleImgRef}
              src={buildAssetRenditionUrl(effectiveCoverUrl) ?? effectiveCoverUrl}
              alt={data.name}
              onLoad={markImgLoaded}
              onError={markImgLoaded}
              className={clsx(
                'absolute inset-0 h-full w-full object-cover transition-[opacity,filter] duration-300 group-hover:brightness-90',
                imgLoaded ? 'opacity-100' : 'opacity-0'
              )}
            />
          </>
        ) : (
          // Empty project — never rendered, or only a blank version poster with no
          // real layer to fall back to: a quiet placeholder, never a black tile.
          <div className="absolute inset-0 flex items-center justify-center">
            <ProjectIcon size={22} strokeWidth={1.6} className="text-[#333333]" />
          </div>
        )}
        {isOpening && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1B1B1B]/50" aria-busy="true">
            <Loader2 size={28} className="animate-spin text-[#ECECEC]" />
          </div>
        )}
      </div>

      <div className="flex items-start gap-1 pl-1 pt-2.5">
        <div className="min-w-0 flex-1">
        <h3 className="flex min-w-0 items-center gap-1.5 font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }} title={data.name}>
          {data.favorite ? <Star size={13} aria-label="Favorite" className="shrink-0 fill-[#ECECEC] stroke-[#ECECEC]" /> : null}
          <span className="truncate" style={{ fontSize: 'inherit' }}>{data.name}</span>
        </h3>
        {/* Project KIND is fixed at creation (Canvas vs List are not interoperable). */}
        <p className="flex min-w-0 items-center gap-1 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>
          <span style={{ fontSize: 'inherit' }}>{STUDIO_PROJECT_KINDS[projectKind].label}</span>
          {!isOwnProject ? <span className="min-w-0 truncate" style={{ fontSize: 'inherit' }}>· {authorName}</span> : null}
          {formattedTime ? <span className="shrink-0" style={{ fontSize: 'inherit' }}>· {formattedTime}</span> : null}
          {formattedCredits ? <span className="shrink-0" style={{ fontSize: 'inherit' }}>· {formattedCredits} credits</span> : null}
        </p>
        </div>
        <RowMoreButton onClick={handleMenuClick} className="-mr-1 -mt-1" />
      </div>
    </div>
  );
});

ProjectRenderer.displayName = 'ProjectRenderer';
